import { DialogTitle, FormControl, Input } from "@mui/joy";
import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormLabel,
  Modal,
  Row,
  Col,
  Image,
  Spinner, 
  Alert,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import { BlogPost, BlogFormData } from "../types/blog";
import BlogService from "../services/blogService.ts";
import { Chip, TextField, Box, Switch, FormControlLabel } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  get_all_sell_Item,
  get_running_auctions,
  get_past_auctions,
} from "../reduxData/user/userAction";
import { useDispatch } from "react-redux";

interface AddEditBlogProps {
  show: boolean;
  handleClose: () => void;
  blogData?: BlogPost | null;
  onSuccess: () => void;
}

const AddEditBlog: React.FC<AddEditBlogProps> = ({
  show,
  handleClose,
  blogData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<BlogFormData>({
    title: "",
    excerpt: "",
    content: "",
    images: [],
    featured_image: "",
    category: "",
    tags: [],
    published: false,
    featured: false,
    related_auction_items: [],
    meta_title: "",
    meta_description: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<string>("");

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Auction items search
  const [auctionSearchQuery, setAuctionSearchQuery] = useState<string>("");
  const [auctionSearchResults, setAuctionSearchResults] = useState<any[]>([]);
  const [selectedAuctionItems, setSelectedAuctionItems] = useState<any[]>([]);
  const [searchingAuctions, setSearchingAuctions] = useState<boolean>(false);

  const dispatch = useDispatch();

  // Search auction items function - uses EXACT same Redux functions as ItemListing.js
  const searchAuctionItems = async (query: string) => {
    if (!query.trim()) {
      setAuctionSearchResults([]);
      return;
    }

    setSearchingAuctions(true);
    try {
      const allResults: any[] = [];

      // Search across all statuses using the EXACT same functions as ItemListing.js
      const statuses = ["pending", "approved", "disapproved", "withdraw"];

      // Search in each status tab using get_all_sell_Item (same as ItemListing.js)
      for (const status of statuses) {
        try {
          const data = await get_all_sell_Item(
            localStorage.getItem("token"),
            dispatch,
            1, // currentPage
            5, // perPage (smaller to get variety from each status)
            status, // currentTab
            "", // queryParams (no category filter)
            query // searchQuery
          );

          if (data?.data?.data) {
            allResults.push(...data.data.data);
          }
        } catch (error) {
          console.log(`Failed to search ${status} items:`, error);
        }
      }

      // Also search in active auctions using get_running_auctions
      try {
        const runningResult = await get_running_auctions(
          dispatch,
          localStorage.getItem("token"),
          1, // page
          5, // limit
          "", // categories
          query // search
        );

        if (runningResult?.data?.data) {
          allResults.push(...runningResult.data.data);
        }
      } catch (error) {
        console.log("Failed to search running auctions:", error);
      }

      // Also search in past auctions using get_past_auctions
      try {
        const pastResult = await get_past_auctions(
          dispatch,
          localStorage.getItem("token"),
          1, // page
          5, // limit
          "", // categories
          query // search
        );

        if (pastResult?.data?.data) {
          allResults.push(...pastResult.data.data);
        }
      } catch (error) {
        console.log("Failed to search past auctions:", error);
      }

      // Remove duplicates based on _id
      const uniqueResults = allResults.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t._id === item._id)
      );

      setAuctionSearchResults(uniqueResults);
    } catch (error) {
      console.error("Error searching auction items:", error);
      setAuctionSearchResults([]);
    } finally {
      setSearchingAuctions(false);
    }
  };

  // Load selected auction items for editing - use data already provided by blog endpoint
  const loadSelectedAuctionItems = (auctionItems: any[]) => {
    if (!auctionItems || !auctionItems.length) return;

    // Filter out any null/undefined items and ensure they have required properties
    const validItems = auctionItems.filter(
      (item) => item && item._id && item.item_name
    );
    setSelectedAuctionItems(validItems);
  };

  // Debounced search - matches ItemListing.js timeout
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAuctionItems(auctionSearchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [auctionSearchQuery]);

  // ReactQuill modules configuration
  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ color: [] }, { background: [] }],
      ["link", "image", "video"],
    ],
  };

  // Initialize form with blog data for editing
  useEffect(() => {
    if (blogData) {
      // Extract auction item IDs for form data
      const auctionItemIds =
        blogData.related_auction_items?.map((item) =>
          typeof item === "string" ? item : item._id
        ) || [];

      setFormData({
        title: blogData.title || "",
        excerpt: blogData.excerpt || "",
        content: blogData.content || "",
        images: blogData.images || [],
        featured_image: blogData.featured_image || "",
        category: blogData.category || "",
        tags: blogData.tags || [],
        published: blogData.published || false,
        featured: blogData.featured || false,
        related_auction_items: auctionItemIds,
        meta_title: blogData.meta_title || "",
        meta_description: blogData.meta_description || "",
      });

      // Load auction items for display - use the actual auction item data from blog response
      if (
        blogData.related_auction_items &&
        blogData.related_auction_items.length > 0
      ) {
        loadSelectedAuctionItems(blogData.related_auction_items);
      }
    } else {
      // Reset form for new blog
      setFormData({
        title: "",
        excerpt: "",
        content: "",
        images: [],
        featured_image: "",
        category: "",
        tags: [],
        published: false,
        featured: false,
        related_auction_items: [],
        meta_title: "",
        meta_description: "",
      });
      setSelectedAuctionItems([]);
    }
    setErrors({});
    setAuctionSearchQuery("");
    setAuctionSearchResults([]);
    setUploadError(null); 
  }, [blogData, show]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSwitchChange =
    (field: "published" | "featured") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.checked,
      }));
    };

  const handleContentChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));

    if (errors.content) {
      setErrors((prev) => ({
        ...prev,
        content: "",
      }));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Handle adding auction item to selection
  const handleAddAuctionItem = (item: any) => {
    // Check if already selected
    if (formData.related_auction_items.includes(item._id)) {
      return;
    }

    // Add to formData
    setFormData((prev) => ({
      ...prev,
      related_auction_items: [...prev.related_auction_items, item._id],
    }));

    // Add to display list
    setSelectedAuctionItems((prev) => [...prev, item]);

    // Clear search
    setAuctionSearchQuery("");
    setAuctionSearchResults([]);
  };

  // Handle removing auction item from selection
  const handleRemoveAuctionItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      related_auction_items: prev.related_auction_items.filter(
        (id) => id !== itemId
      ),
    }));

    setSelectedAuctionItems((prev) =>
      prev.filter((item) => item._id !== itemId)
    );
  };

    const handleFeaturedImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await BlogService.uploadFeaturedImage(file);
      if (response.status) {
        // On success, set the returned URL in your form data
        setFormData({ ...formData, featured_image: response.imageUrl });
        toast.success("Image uploaded successfully!");
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "An unexpected error occurred.";
      setUploadError(errorMessage);
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = blogData
        ? await BlogService.updateBlogPost(blogData._id, formData)
        : await BlogService.createBlogPost(formData);

      if (response.status) {
        toast.success(`Blog ${blogData ? "updated" : "created"} successfully`);
        onSuccess();
        handleCancel();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error saving blog:", error);
      toast.error(error.response?.data?.error || "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      images: [],
      featured_image: "",
      category: "",
      tags: [],
      published: false,
      featured: false,
      related_auction_items: [],
      meta_title: "",
      meta_description: "",
    });
    setErrors({});
    setNewTag("");
    setAuctionSearchQuery("");
    setAuctionSearchResults([]);
    setSelectedAuctionItems([]);
    setUploadError(null); 
    handleClose();
  };

  return (
    <Modal show={show} size="lg" backdrop="static">
      <Modal.Body>
        <DialogTitle>{blogData ? "Edit" : "Add"} Blog Post</DialogTitle>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={8}>
              {/* Title */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">
                  Title <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  type="text"
                  name="title"
                  placeholder="Enter blog title"
                  value={formData.title}
                  onChange={handleInputChange}
                />
                {errors.title && (
                  <div className="error_msg">{errors.title}</div>
                )}
              </FormControl>

              {/* Excerpt */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Excerpt</FormLabel>
                <textarea
                  name="excerpt"
                  className="form-control"
                  rows={3}
                  placeholder="Brief description of the blog post..."
                  value={formData.excerpt}
                  onChange={handleInputChange}
                />
              </FormControl>

              {/* Content */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">
                  Content <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <ReactQuill
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={modules}
                  style={{ height: "300px", marginBottom: "50px" }}
                />
                {errors.content && (
                  <div className="error_msg">{errors.content}</div>
                )}
              </FormControl>
            </Col>

            <Col md={4}>
              {/* Category */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Category</FormLabel>
                <Input
                  type="text"
                  name="category"
                  placeholder="Enter category (e.g., gaming, news, guides...)"
                  value={formData.category}
                  onChange={handleInputChange}
                />
              </FormControl>

              {/* Tags */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Tags</FormLabel>
                <Box className="mb-2">
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add tags..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </Box>
                <Box className="d-flex flex-wrap gap-1">
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      onDelete={() => handleRemoveTag(tag)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Box>
              </FormControl>

              {/* Related Auction Items */}
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Related Auction Items</FormLabel>

                {/* Search Input */}
                <Box className="mb-2">
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search auction items..."
                    value={auctionSearchQuery}
                    onChange={(e) => setAuctionSearchQuery(e.target.value)}
                    disabled={searchingAuctions}
                  />
                </Box>

                {/* Search Results */}
                {auctionSearchQuery && (
                  <Box
                    className="mb-2"
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  >
                    {searchingAuctions ? (
                      <div className="p-2 text-center">Searching...</div>
                    ) : auctionSearchResults.length > 0 ? (
                      auctionSearchResults
                        .filter((item) => item && item._id)
                        .map((item) => (
                          <div
                            key={item._id}
                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                            onClick={() => handleAddAuctionItem(item)}
                            style={{
                              cursor: "pointer",
                              backgroundColor:
                                formData.related_auction_items.includes(
                                  item._id
                                )
                                  ? "#f8f9fa"
                                  : "transparent",
                            }}
                            onMouseEnter={(e) =>
                              ((e.target as HTMLElement).style.backgroundColor =
                                "#f8f9fa")
                            }
                            onMouseLeave={(e) => {
                              (e.target as HTMLElement).style.backgroundColor =
                                formData.related_auction_items.includes(
                                  item._id
                                )
                                  ? "#f8f9fa"
                                  : "transparent";
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <img
                                src={
                                  item.photos?.[0] ||
                                  item.thumbnails?.[0] ||
                                  "/default-auction.png"
                                }
                                alt={item.item_name}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                                className="me-2"
                              />
                              <div>
                                <div className="fw-600 small">
                                  {item.item_name}
                                </div>
                                <div className="text-muted small">
                                  {item.reserver_price &&
                                  item.reserver_price !== "0"
                                    ? `$${item.reserver_price}`
                                    : "No reserve"}
                                  {item.status && (
                                    <span
                                      className={`ms-2 badge badge-sm ${
                                        item.status === "approved"
                                          ? "bg-success"
                                          : item.status === "pending"
                                          ? "bg-warning"
                                          : item.status === "disapproved"
                                          ? "bg-danger"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {item.status}
                                    </span>
                                  )}
                                  {formData.related_auction_items.includes(
                                    item._id
                                  ) && (
                                    <span className="ms-2 text-success">
                                      ✓ Selected
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="p-2 text-center text-muted">
                        No auction items found
                      </div>
                    )}
                  </Box>
                )}

                {/* Selected Items */}
                {selectedAuctionItems.length > 0 && (
                  <Box className="mb-2">
                    <div className="small fw-600 mb-1">
                      Selected Items ({selectedAuctionItems.length}):
                    </div>
                    <Box className="d-flex flex-wrap gap-1">
                      {selectedAuctionItems
                        .filter((item) => item && item._id)
                        .map((item) => (
                          <Chip
                            key={item._id}
                            label={
                              <div className="d-flex align-items-center">
                                <img
                                  src={
                                    item.photos?.[0] ||
                                    item.thumbnails?.[0] ||
                                    "/default-auction.png"
                                  }
                                  alt={item.item_name}
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    objectFit: "cover",
                                    borderRadius: "2px",
                                  }}
                                  className="me-1"
                                />
                                <span>{item.item_name}</span>
                              </div>
                            }
                            size="small"
                            onDelete={() => handleRemoveAuctionItem(item._id)}
                            deleteIcon={<CloseIcon />}
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                    </Box>
                  </Box>
                )}
              </FormControl>

              {/* Featured Image */}
             <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Featured Image</FormLabel>

                {/* Image Preview */}
                {formData.featured_image && !isUploading && (
                  <div className="mb-2 p-2 border rounded">
                    <Image src={formData.featured_image} alt="Featured preview" fluid thumbnail/>
                    <p className="small text-muted mt-1 mb-0">Current Image</p>
                  </div>
                )}

                {/* Loading Spinner */}
                {isUploading && (
                  <div className="d-flex align-items-center mb-2">
                    <Spinner animation="border" size="sm" role="status" />
                    <span className="ms-2">Uploading...</span>
                  </div>
                )}
                
                {/* File Input */}
                <Form.Control
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    onChange={handleFeaturedImageChange}
                    disabled={isUploading}
                />
                
                {/* Error Message */}
                {uploadError && (
                    <Alert variant="danger" className="mt-2 p-2 small">
                        {uploadError}
                    </Alert>
                )}
              </FormControl>

              {/* Status Switches */}
              <FormControl className="mt-1 mb-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.published}
                      onChange={handleSwitchChange("published")}
                    />
                  }
                  label="Published"
                />
              </FormControl>

              <FormControl className="mt-1 mb-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured}
                      onChange={handleSwitchChange("featured")}
                    />
                  }
                  label="Featured"
                />
              </FormControl>
            </Col>
          </Row>

          {/* SEO Section */}
          <Row>
            <Col md={12}>
              <h6 className="fw-600 mt-3 mb-2">SEO Settings</h6>
              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Meta Title</FormLabel>
                <Input
                  type="text"
                  name="meta_title"
                  placeholder="SEO title..."
                  value={formData.meta_title}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl className="mt-1 mb-2">
                <FormLabel className="fw-600">Meta Description</FormLabel>
                <textarea
                  name="meta_description"
                  className="form-control"
                  rows={2}
                  placeholder="SEO description..."
                  value={formData.meta_description}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            <Button
              type="submit"
              className="btn common-button me-2"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
            <Button
              className="question-button"
              variant="danger"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditBlog;
